<template>
    <div ref="menu-container" class="Treeelect-dropdown vue-treeselect__menu-container" :style="menuContainerStyle">
        <Transition name="vue-treeselect__menu--transition">
            <div v-if="instance.menu.isOpen" :key="instance.key" ref="menu" class="vue-treeselect__menu scroll-content" :style="menuStyle" @mousedown="instance.handleMouseDown">
                <template v-if="instance.$slots['before-list']">
                    <component :is="instance.$slots['before-list']" />
                </template>

                <template v-if="instance.async">
                    <Tip v-if="shouldShowSearchPromptTip" type="search-prompt" icon="warning">
                        {{instance.searchPromptText}}
                    </Tip>

                    <Tip v-else-if="entry.isLoading" type="loading" icon="loader">
                        {{instance.loadingText}}
                    </Tip>

                    <Tip v-else-if="entry.loadingError" type="error" icon="error">
                        {{entry.loadingError}}
                        <a class="vue-treeselect__retry Anchor" :title="instance.retryTitle" @click="instance.handleRemoteSearch">
                            {{instance.retryText}}
                        </a>
                    </Tip>

                    <Tip v-else-if="shouldShowNoResultsTip" type="no-results" icon="warning">
                        {{instance.noResultsText}}
                    </Tip>

                    <div v-else class="vue-treeselect__list">
                        <div v-if="instance.selectAllOption" class="vue-treeselect__list-item">
                            <div class="vue-treeselect__option vue-treeselect__option--selected" data-id="-1">
                                <div class="vue-treeselect__option-arrow-placeholder">&nbsp;</div>
                                <div class="vue-treeselect__label-container">
                                    <div class="vue-treeselect__checkbox-container">
                                        <span class="vue-treeselect__checkbox vue-treeselect__checkbox--checked">
                                            <span class="vue-treeselect__check-mark"></span>
                                            <span class="vue-treeselect__minus-mark"></span>
                                        </span>
                                    </div>
                                    <label class="vue-treeselect__label">Tous</label>
                                </div>
                            </div>
                        </div>

                        <Option v-for="rootNode of instance.forest.normalizedOptions" :node="rootNode" :key="rootNode.id" />
                    </div>
                </template>
                <template v-else>
                    <template v-if="instance.localSearch.active">
                        <Tip v-if="instance.rootOptionsStates.isLoading" type="loading" icon="loader">
                            {{instance.loadingText}}
                        </Tip>

                        <Tip v-else-if="instance.rootOptionsStates.loadingError" type="error" icon="error">
                            {{instance.rootOptionsStates.loadingError}}
                            <a class="vue-treeselect__retry" @click="instance.loadRootOptions">
                                {{instance.retryText}}
                            </a>
                        </Tip>

                        <Tip v-else-if="instance.rootOptionsStates.isLoaded && instance.forest.normalizedOptions.length === 0" type="no-options" icon="warning">
                            {{instance.noOptionsText}}
                        </Tip>

                        <Tip v-else-if="instance.localSearch.noResults" type="no-results" icon="warning">
                            {{instance.noResultsText}}
                        </Tip>

                        <div v-else class="vue-treeselect__list">
                            <div v-if="instance.selectAllOption" class="vue-treeselect__list-item">
                                <div class="vue-treeselect__option vue-treeselect__option--selected" data-id="-1">
                                    <div class="vue-treeselect__option-arrow-placeholder">&nbsp;</div>
                                    <div class="vue-treeselect__label-container">
                                        <div class="vue-treeselect__checkbox-container">
                                            <span class="vue-treeselect__checkbox vue-treeselect__checkbox--checked">
                                                <span class="vue-treeselect__check-mark"></span>
                                                <span class="vue-treeselect__minus-mark"></span>
                                            </span>
                                        </div>
                                        <label class="vue-treeselect__label">Tous</label>
                                    </div>
                                </div>
                            </div>

                            <Option v-for="rootNode of instance.forest.normalizedOptions" :node="rootNode" :key="rootNode.id" />
                        </div>
                    </template>
                    <template v-else>
                        <Tip v-if="instance.rootOptionsStates.isLoading" type="loading" icon="loader">
                            {{instance.loadingText}}
                        </Tip>

                        <Tip v-else-if="instance.rootOptionsStates.loadingError" type="error" icon="error">
                            {{instance.rootOptionsStates.loadingError}}
                            <a class="vue-treeselect__retry" @click="instance.loadRootOptions">
                                {{instance.retryText}}
                            </a>
                        </Tip>

                        <Tip v-else-if="instance.rootOptionsStates.isLoaded && instance.forest.normalizedOptions.length === 0" type="no-options" icon="warning">
                            {{instance.noOptionsText}}
                        </Tip>

                        <div v-else class="vue-treeselect__list">
                            <div v-if="instance.selectAllOption" class="vue-treeselect__list-item">
                                <div class="vue-treeselect__option vue-treeselect__option--selected" data-id="-1">
                                    <div class="vue-treeselect__option-arrow-placeholder">&nbsp;</div>
                                    <div class="vue-treeselect__label-container">
                                        <div class="vue-treeselect__checkbox-container">
                                            <span class="vue-treeselect__checkbox vue-treeselect__checkbox--checked">
                                                <span class="vue-treeselect__check-mark"></span>
                                                <span class="vue-treeselect__minus-mark"></span>
                                            </span>
                                        </div>
                                        <label class="vue-treeselect__label">Tous</label>
                                    </div>
                                </div>
                            </div>

                            <Option v-for="rootNode of instance.forest.normalizedOptions" :node="rootNode" :key="rootNode.id" />
                        </div>
                    </template>
                </template>

                <template v-if="instance.$slots['after-list']">
                    <component :is="instance.$slots['after-list']" />
                </template>
            </div>
        </Transition>
    </div>
</template>

<script>
import { MENU_BUFFER } from '../constants';
import { watchSize, setupResizeAndScrollEventListeners } from '../utils';

import Option   from './Option.vue';
import Tip      from './Tip.vue';

const directionMap =
{
    top   : 'top',
    bottom: 'bottom',
    above : 'top',
    below : 'bottom'
}

export default
{
    name       : 'Treeselect--menu',
    inject     : ['instance'],
    components : { Option, Tip },
    computed   :
    {
        menuContainerStyle()
        {
            const { instance } = this;

            return { zIndex: instance.appendToBody ? null : instance.zIndex };
        },

        menuStyle()
        {
            const { instance } = this;

            return { maxHeight: instance.maxHeight + 'px' };
        },

        entry()
        {
            const { instance } = this;

            return instance.getRemoteSearchEntry();
        },

        shouldShowSearchPromptTip()
        {
            const { instance } = this;

            return instance.trigger.searchQuery === ''; // && !instance.defaultOptions && instance.minChar > 0;
        },

        shouldShowNoResultsTip()
        {
            return this.shouldShowSearchPromptTip ? false : this.entry.isLoaded && this.entry.options.length === 0;
        },
    },

    watch:
    {
        'instance.menu.isOpen'( newValue )
        {
            if( newValue )
            {
                this.$nextTick( this.onMenuOpen ); // In case `openMenu()` is just called and the menu is not rendered yet.
            }
            else
            {
                this.onMenuClose();
            }
        },

        'instance.forest'( newValue )
        {
            console.log( 'instance forest', newValue );
        }
    },

    created()
    {
        this.menuSizeWatcher = null;
        this.menuResizeAndScrollEventListeners = null;
    },

    mounted()
    {
        const { instance } = this;

        if( instance.menu.isOpen )
        {
            this.$nextTick( this.onMenuOpen );
        }
    },

    unmounted()
    {
        this.onMenuClose();
    },

    methods:
    {
        onMenuOpen()
        {
            this.adjustMenuOpenDirection();
            this.setupMenuSizeWatcher();
            this.setupMenuResizeAndScrollEventListeners();
        },

        onMenuClose()
        {
            this.removeMenuSizeWatcher();
            this.removeMenuResizeAndScrollEventListeners();
        },

        adjustMenuOpenDirection()
        {
            const { instance } = this;

            if( !instance.menu.isOpen )
            { return }

            const $menu          = instance.getMenu();
            const $control       = instance.getControl();
            const menuRect       = $menu.getBoundingClientRect();
            const controlRect    = $control.getBoundingClientRect();
            const menuHeight     = menuRect.height;
            const viewportHeight = window.innerHeight;
            const spaceAbove     = controlRect.top;
            const spaceBelow     = window.innerHeight - controlRect.bottom;

            const isControlInViewport = ( ( controlRect.top >= 0 && controlRect.top <= viewportHeight ) || ( controlRect.top < 0 && controlRect.bottom > 0 ) );

            const hasEnoughSpaceBelow = spaceBelow > menuHeight + MENU_BUFFER;
            const hasEnoughSpaceAbove = spaceAbove > menuHeight + MENU_BUFFER;

            if( !isControlInViewport )
            {
                instance.closeMenu();
            }
            else if( instance.openDirection !== 'auto' )
            {
                instance.menu.placement = directionMap[ instance.openDirection ];
            }
            else if( hasEnoughSpaceBelow || !hasEnoughSpaceAbove )
            {
                instance.menu.placement = 'bottom';
            }
            else
            {
                instance.menu.placement = 'top';
            }
        },

        setupMenuSizeWatcher()
        {
            const { instance } = this;

            const $menu = instance.getMenu();

            if( this.menuSizeWatcher )
            { return }

            this.menuSizeWatcher = { remove: watchSize( $menu, this.adjustMenuOpenDirection ) };
        },

        setupMenuResizeAndScrollEventListeners()
        {
            const { instance } = this;

            const $control = instance.getControl();

            if( this.menuResizeAndScrollEventListeners )
            { return }

            this.menuResizeAndScrollEventListeners = { remove: setupResizeAndScrollEventListeners( $control, this.adjustMenuOpenDirection ) }
        },

        removeMenuSizeWatcher()
        {
            if( !this.menuSizeWatcher )
            { return }

            this.menuSizeWatcher.remove();

            this.menuSizeWatcher = null;
        },

        removeMenuResizeAndScrollEventListeners()
        {
            if( !this.menuResizeAndScrollEventListeners )
            { return }

            this.menuResizeAndScrollEventListeners.remove();

            this.menuResizeAndScrollEventListeners = null;
        }
    }
}
</script>
